import { supabase } from '@/integrations/supabase/client';

export interface ExtensionJob {
  id: string;
  status: 'pending' | 'running' | 'processing' | 'completed' | 'failed';
  board_id: string | null;
  result: Record<string, unknown> | null;
  error_message: string | null;
}

const TERMINAL = new Set(['completed', 'failed']);

/**
 * Poll an extension_jobs row until it completes or fails. Resolves with the
 * final row, or null if it is still running after `timeoutMs`.
 */
export async function watchExtensionJob(
  jobId: string,
  { intervalMs = 4000, timeoutMs = 8 * 60 * 1000 }: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<ExtensionJob | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    const { data, error } = await supabase
      .from('extension_jobs')
      .select('id, status, board_id, result, error_message')
      .eq('id', jobId)
      .maybeSingle();
    if (error) {
      console.warn('watchExtensionJob: read failed, retrying', error);
      continue;
    }
    if (data && TERMINAL.has(data.status)) return data as ExtensionJob;
  }
  return null;
}

/** Runs of an extension in the last 24 hours, for "N runs left today". */
export async function countRecentRuns(extensionId: string): Promise<number | null> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from('extension_jobs')
    .select('id', { count: 'exact', head: true })
    .eq('extension_id', extensionId)
    .gte('created_at', since);
  if (error) return null;
  return count ?? 0;
}

/**
 * supabase.functions.invoke wraps non-2xx replies in an error whose body is on
 * `context`. Pull out the function's own `{ error, code }` when there is one.
 */
export async function readFunctionError(error: unknown): Promise<{ message: string; code?: string }> {
  const context = (error as { context?: unknown })?.context;
  if (context instanceof Response) {
    try {
      const body = await context.clone().json();
      if (body?.error) return { message: String(body.error), code: body.code };
    } catch {
      // fall through to the generic message
    }
  }
  return { message: error instanceof Error ? error.message : 'Something went wrong' };
}
